import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import * as Dialog from "@radix-ui/react-dialog";
import { motion } from "framer-motion";
import { Play, X, ArrowRight } from "lucide-react";

/* Neuer Robbie-Imagefilm (Thomas): Spot mit Ton (Lightbox + eigene Sektion) und
   stumme Hero-Schleife. Dateien liegen web-optimiert unter /media/robbie/. */

const H = { fontFamily: "'Outfit', 'Poppins', sans-serif", fontWeight: 800 };
const B = { fontFamily: "'Poppins', sans-serif" };
const CREME = "#FAF8F3";
const NEON = "#FFD60A";

export const SPOT_MP4 = "/media/robbie/spot.mp4";
export const SPOT_WEBM = "/media/robbie/spot.webm";
export const SPOT_POSTER = "/media/robbie/spot-poster.webp";

const up = (d = 0) => ({ initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.55, delay: d, ease: [0.22, 1, 0.36, 1] } });

/* ---------- Lightbox: voller Spot MIT Ton, startet beim Öffnen (Klick = User-Geste) ---------- */
export const SpotLightbox = ({ open, onOpenChange, returnFocusRef }) => {
  const ref = useRef(null);
  useEffect(() => {
    const v = ref.current;
    if (!open || !v) return undefined;
    try { v.currentTime = 0; v.muted = false; } catch { /* egal */ }
    const p = v.play();
    if (p && p.catch) p.catch(() => {});
    return () => { try { v.pause(); } catch { /* egal */ } };
  }, [open]);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[300] bg-[#0B0B0B]/92 backdrop-blur-md data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0" data-testid="spot-lightbox-backdrop" />
        <Dialog.Content
          onCloseAutoFocus={(e) => { if (returnFocusRef?.current) { e.preventDefault(); returnFocusRef.current.focus(); } }}
          className="fixed inset-0 z-[301] flex items-center justify-center p-0 outline-none sm:p-6 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0"
          data-testid="spot-lightbox">
          <Dialog.Title className="sr-only">Robbie in 30 Sekunden</Dialog.Title>
          <Dialog.Description className="sr-only">Der Robbie-Imagefilm mit Ton.</Dialog.Description>
          <Dialog.Close
            className="absolute right-3 top-3 z-10 grid h-11 w-11 place-items-center rounded-full text-[#111] shadow-[0_4px_0_0_rgba(0,0,0,0.4)] transition-transform hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            style={{ background: NEON }} aria-label="Video schließen" data-testid="spot-lightbox-close">
            <X size={20} strokeWidth={2.5} />
          </Dialog.Close>
          {open && (
            <video ref={ref} controls autoPlay playsInline preload="auto" poster={SPOT_POSTER}
              aria-label="Robbie in 30 Sekunden"
              className="h-full max-h-[100dvh] w-full bg-black object-contain sm:h-auto sm:max-h-[90dvh] sm:w-auto sm:max-w-[min(100%,calc(90dvh*9/16))] sm:rounded-[24px] sm:shadow-[0_40px_80px_-30px_rgba(0,0,0,0.8)]"
              data-testid="spot-lightbox-video">
              <source src={SPOT_WEBM} type="video/webm" />
              <source src={SPOT_MP4} type="video/mp4" />
            </video>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

/* ---------- Inline-Player: Poster + großer Play-Button, Ton nach Klick, preload=none ---------- */
const SpotPlayer = () => {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const start = () => {
    const v = ref.current;
    if (!v) return;
    setPlaying(true);
    try { v.muted = false; } catch { /* egal */ }
    const p = v.play();
    if (p && p.catch) p.catch(() => {});
  };
  return (
    <div className="relative mx-auto aspect-[9/16] w-full max-w-[300px] overflow-hidden rounded-[28px] border-2 border-[#111] bg-black shadow-[8px_8px_0_0_#111]" data-testid="spot-section-player">
      <video ref={ref} poster={SPOT_POSTER} controls={playing} playsInline preload="none"
        className="h-full w-full object-cover" data-testid="spot-section-video" onEnded={() => setPlaying(false)}>
        <source src={SPOT_WEBM} type="video/webm" />
        <source src={SPOT_MP4} type="video/mp4" />
      </video>
      {!playing && (
        <button type="button" onClick={start} aria-label="Robbie-Imagefilm abspielen" data-testid="spot-section-play"
          className="absolute inset-0 grid place-items-center bg-black/20 transition-colors hover:bg-black/30 focus:outline-none">
          <span className="grid h-20 w-20 place-items-center rounded-full border-2 border-[#111] text-[#111] shadow-[0_6px_0_0_#111] transition-transform group-hover:scale-105" style={{ background: NEON }}>
            <Play size={34} strokeWidth={2.5} fill="currentColor" className="ml-1" />
          </span>
        </button>
      )}
    </div>
  );
};

/* ---------- Sektion „Robbie in 30 Sekunden" (direkt unter dem Hero) ---------- */
export const RobbieSpotSection = () => (
  <section id="robbie-film" className="scroll-mt-24" style={{ background: CREME }} data-testid="robbie-spot-section">
    <div className="mx-auto grid max-w-[1180px] items-center gap-10 px-6 py-12 md:px-10 md:py-20 lg:grid-cols-[1fr_auto] lg:gap-16">
      <motion.div {...up(0)} className="order-2 flex flex-col items-start lg:order-1">
        <span style={{ ...B, fontWeight: 700 }} className="inline-flex items-center gap-2 rounded-full border-2 border-[#111] bg-[#FDD105] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.12em] text-[#111]" data-testid="robbie-spot-eyebrow">
          <Play size={12} strokeWidth={2.8} fill="currentColor" aria-hidden="true" /> Imagefilm
        </span>
        <h2 style={H} className="mt-4 text-3xl leading-tight text-[#111] md:text-4xl" data-testid="robbie-spot-headline">
          Robbie in <span className="box-decoration-clone bg-[#FFD60A] px-2">30 Sekunden.</span>
        </h2>
        <p style={B} className="mt-4 max-w-[42ch] text-[16px] leading-relaxed text-[#4A4A4A]" data-testid="robbie-spot-lead">
          Ein Foto – Robbie macht den Rest.
        </p>
        <Link to="/kunden/registrieren" data-testid="robbie-spot-cta" style={{ ...B, fontWeight: 700 }}
          className="mt-7 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border-2 border-[#111] bg-[#FFD60A] px-6 py-3.5 text-[15px] font-bold text-[#111] shadow-[0_6px_0_0_#111] transition-transform hover:-translate-y-0.5 active:translate-y-0 active:shadow-[0_3px_0_0_#111]">
          30 Tage kostenlos testen <ArrowRight size={18} strokeWidth={2.5} />
        </Link>
      </motion.div>
      <motion.div {...up(0.1)} className="order-1 flex justify-center lg:order-2">
        <SpotPlayer />
      </motion.div>
    </div>
  </section>
);
